import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import InventoryIcon from '@mui/icons-material/Inventory';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HeroSection from '../components/HeroSection';
import FeaturesSection from '../components/FeaturesSection';
import ForWhomSection from '../components/ForWhomSection';
import AboutSection from '../components/AboutSection';
import PricingSection from '../components/PricingSection';
import ContactSection from '../components/ContactSection';
import RequestDemoForm from '../components/RequestDemoForm';
import Footer from '../components/Footer';
import LanguageSwitcher from '../../../components/LanguageSwitcher';

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  const menuItems = [
    { label: t('home'), id: 'home' },
    { label: t('features'), id: 'features' },
    { label: t('for_whom'), id: 'for-whom' },
    { label: t('about'), id: 'about' },
    { label: t('pricing'), id: 'pricing' },
    { label: t('contact'), id: 'contact' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <Box>
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(20px)',
          boxShadow: `0 2px 30px ${alpha(theme.palette.primary.main, 0.08)}`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              <ReceiptLongIcon 
                sx={{ 
                  fontSize: 32, 
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }} 
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.5px',
                }}
              >
                Billeur
              </Typography>
            </Box>
          </Box>

          {!isMobile ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                      px: 2,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        transition: 'width 0.3s',
                      },
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        '&::before': {
                          width: '80%',
                        },
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              <LanguageSwitcher />
              <Button
                variant="contained"
                onClick={handleLoginClick}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  ml: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                }}
              >
                {t('login')}
              </Button>
            </>
          ) : (
            <>
              <LanguageSwitcher />
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: theme.palette.text.primary, ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 250,
            pt: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              sx={{
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <ListItemText
                primary={item.label}
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: 500,
                  },
                }}
              />
            </ListItem>
          ))}
          <ListItem>
            <Button
              fullWidth
              variant="contained"
              onClick={handleLoginClick}
              sx={{ borderRadius: 2, mt: 2 }}
            >
              {t('login')}
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ pt: 8 }}>
        <div id="home">
          <HeroSection onLoginClick={handleLoginClick} />
        </div>
        <div id="features">
          <FeaturesSection />
        </div>
        <div id="for-whom">
          <ForWhomSection />
        </div>
        <div id="about">
          <AboutSection />
        </div>
        <div id="pricing">
          <PricingSection />
        </div>
        <div id="demo">
          <RequestDemoForm />
        </div>
        <div id="contact">
          <ContactSection />
        </div>
      </Box>

      {/* Footer */}
      <Footer scrollToSection={scrollToSection} />
    </Box>
  );
};

export default HomePage;
